// 전화번호 연결 페이지 - Supabase Auth 기반

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';

export default function PhoneLinkPage() {
  const navigate = useNavigate();
  const { user } = useSupabaseParentAuth();

  const [phone, setPhone] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');
  const [linkedChildren, setLinkedChildren] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return numbers.slice(0, 3) + '-' + numbers.slice(3);
    return numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleLinkPhone = async () => {
    if (!user) {
      setError('로그인 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
      return;
    }

    if (!phone || phone.length < 12) {
      setError('올바른 전화번호를 입력해주세요.');
      return;
    }

    setError('');
    setIsLinking(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');

      // 백엔드 API 호출 (POST /api/v1/parent/auth/link-phone)
      const response = await fetch('/api/v1/parent/auth/link-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          phone: cleanPhone,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '전화번호 연결에 실패했습니다.');
      }

      const data = await response.json();


      // 연결된 자녀 목록 표시
      if (data.linked_children && data.linked_children.length > 0) {
        setLinkedChildren(data.linked_children.map((child: any) => child.name));
        setShowSuccess(true);

        // 2초 후 대시보드로 이동
        setTimeout(() => {
          navigate('/parent/dashboard');
        }, 2000);
      } else {
        setError('해당 전화번호로 등록된 자녀를 찾을 수 없습니다. 독서실 관리자에게 문의해주세요.');
      }
    } catch (err: any) {
      console.error('❌ 전화번호 연결 실패:', err);
      setError(err.message || '전화번호 연결에 실패했습니다.');
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
                NewAttend
              </div>
              <span className="text-sm text-purple-600 font-medium">학부모</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Phone Link Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {/* Title */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">전화번호 연결</h2>
              <p className="mt-2 text-sm text-gray-600">
                자녀와 연결하려면 등록된 전화번호를 입력하세요
              </p>
            </div>

            {/* Success Alert */}
            {showSuccess && linkedChildren.length > 0 && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <p className="font-semibold mb-2">자녀 연결 완료!</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  {linkedChildren.map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs">잠시 후 대시보드로 이동합니다...</p>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Phone Input Form */}
            {!showSuccess && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    전화번호
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="010-1234-5678"
                    value={phone}
                    onChange={handlePhoneChange}
                    disabled={isLinking}
                    maxLength={13}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    독서실 등록 시 입력한 학부모 전화번호를 입력하세요
                  </p>
                </div>

                <button
                  onClick={handleLinkPhone}
                  disabled={!phone || phone.length < 12 || isLinking}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
                >
                  {isLinking ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      자녀 검색 중...
                    </span>
                  ) : (
                    '자녀 연결하기'
                  )}
                </button>
              </div>
            )}

            {/* Help Text */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
              <p className="font-semibold text-purple-900 mb-2">도움말</p>
              <ul className="space-y-1 text-purple-700 text-xs">
                <li>• 독서실 등록 시 입력한 학부모 전화번호를 입력하세요</li>
                <li>• 해당 번호로 등록된 모든 자녀가 자동으로 연결됩니다</li>
                <li>• 전화번호가 일치하지 않으면 독서실 관리자에게 문의하세요</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
